// Timeline <audio> elements play through Howler's AudioContext: routed that
// way an element starts within a few ms of play() and keeps its pitch at any
// rate. This module owns the pieces that are shared: the routing itself, keeping the context awake while a routed
// element sounds, and the fallback for sources the browser may not route.
import { Howler } from "howler";
import { credentialsFor } from "./utils.es6";

const routed = new WeakMap(); // element -> { source, onPlay, onStop, holding }
let holders = 0;
let autoSuspendBefore = true;

export function sharedAudioContext() {
  if (!Howler.usingWebAudio) return null;
  // Howler creates its context lazily; reading the volume sets it up.
  if (!Howler.ctx) Howler.volume();
  // Howler's first unlock (at the first new Howl) closes the context and opens
  // a new one when its sample rate is not 44100. An element routed before that
  // stays on the closed one and never advances, so the unlock runs first.
  if (Howler.ctx && Howler.autoUnlock && typeof Howler._unlockAudio === "function") {
    Howler._unlockAudio();
  }
  return Howler.usingWebAudio ? Howler.ctx : null;
}

// Howler suspends the context after 30 s without one of ITS sounds; a routed
// element would freeze with it. While any routed element plays, that is off.
function hold() {
  if (holders++ > 0) return;
  autoSuspendBefore = Howler.autoSuspend;
  Howler.autoSuspend = false;
  Howler._autoResume();
  const ctx = Howler.ctx;
  if (ctx && ctx.state === "suspended" && Howler.state === "running") {
    Promise.resolve(ctx.resume()).catch(() => {});
  }
}

function release() {
  if (holders === 0 || --holders > 0) return;
  Howler.autoSuspend = autoSuspendBefore;
  Howler._autoSuspend(); // back to Howler's own 30 s rule
}

// Opens a suspended shared context now — in the task of the play request
// (the user's gesture) instead of on a routed element's `play` event, which
// only comes once the main thread is free. It wakes the context and nothing
// else: no playback is started when it opens, and no hold is taken, so
// Howler's own idle rule applies again if nothing ends up playing.
export function wakeAudioGraph() {
  const ctx = Howler.usingWebAudio ? Howler.ctx : null;
  if (!ctx || typeof ctx.resume !== "function") return;
  // "interrupted": Safari/iOS after a call or another app took the audio.
  if (ctx.state !== "suspended" && ctx.state !== "interrupted") return;
  Howler._autoResume(); // Howler's own suspend, and the interrupted case
  if (ctx.state === "suspended" && Howler.state === "running") {
    Promise.resolve(ctx.resume()).catch(() => {});
  }
  if (holders === 0) Howler._autoSuspend();
}

// Call first in the press handler: some browsers resume only inside the gesture.
export function unlockAudioGraph() {
  if (!sharedAudioContext()) return;
  wakeAudioGraph();
}

export const isRouted = (el) => routed.has(el);

// Connects `el` to the shared context. Must happen before it plays; the
// element then sounds only through the graph.
function route(el) {
  const ctx = sharedAudioContext();
  if (!ctx || typeof ctx.createMediaElementSource !== "function") return false;
  let source;
  try {
    source = ctx.createMediaElementSource(el);
    source.connect(ctx.destination);
  } catch (_) {
    return false;
  }
  const entry = { source, holding: false };
  entry.onPlay = () => {
    if (!entry.holding) {
      entry.holding = true;
      hold();
    }
  };
  entry.onStop = () => {
    if (entry.holding) {
      entry.holding = false;
      release();
    }
  };
  el.addEventListener("play", entry.onPlay);
  ["pause", "ended", "emptied", "error"].forEach((n) => el.addEventListener(n, entry.onStop));
  routed.set(el, entry);
  return true;
}

// Call when the element is thrown away.
export function unrouteAudio(el) {
  const entry = routed.get(el);
  if (!entry) return;
  el.removeEventListener("play", entry.onPlay);
  ["pause", "ended", "emptied", "error"].forEach((n) => el.removeEventListener(n, entry.onStop));
  // A late load error of a discarded element must not bring a new one to life.
  if (entry.onPlaying) el.removeEventListener("playing", entry.onPlaying);
  if (entry.onError) el.removeEventListener("error", entry.onError);
  entry.onStop();
  try {
    entry.source.disconnect();
  } catch (_) {
    /* already gone */
  }
  routed.delete(el);
}

// An <audio> for `url`, routed when possible. Routing needs a CORS-readable
// source: one without the headers fails to LOAD with crossOrigin set (and
// would be silent if routed without it). In that case — before anything has
// played — `onFallback(plain, failed)` receives a fresh, plain element for
// the same url. unrouteAudio(el) cancels this as well.
export function openTimelineAudio(url, onFallback) {
  const el = new Audio();
  // "anonymous" sends no cookie, so our gated /api/f/ assets 401 before the fallback.
  el.crossOrigin = credentialsFor(url).credentials ? "use-credentials" : "anonymous";
  if (!route(el)) return new Audio(url);
  const entry = routed.get(el);
  let played = false;
  entry.onPlaying = () => (played = true);
  entry.onError = () => {
    if (played) return; // a later network error is not a CORS refusal
    unrouteAudio(el);
    onFallback(new Audio(url), el);
  };
  el.addEventListener("playing", entry.onPlaying, { once: true });
  el.addEventListener("error", entry.onError, { once: true });
  el.src = url;
  return el;
}
